/**
 * Every user-facing string the UI says. Copy is the one thing here that does not live where it is
 * consumed: a sentence can be said by any number of surfaces, and splitting it by consumer
 * duplicates the fragments they have in common.
 *
 * Names read `<SURFACE>_<INTENT>`. Interpolated sentences are functions rather than templates
 * assembled at the call site, so the whole sentence stays here and stays translatable.
 *
 * Log and telemetry text is NOT copy and does not belong here — it stays with the code that emits
 * it, next to the error it describes.
 */

/* -------------------------------------------------------------------------- */
/* Shared                                                                     */
/* -------------------------------------------------------------------------- */

/** Closes every delete notice, so the reassurance reads the same wherever it appears. */
const DELETE_NOTICE_CLOSER = "You can add it again anytime.";

/** With no project, reports and insights have the same requirement and should read alike. */
export const NO_PROJECT_EMPTY_STATE = "Load project data to get started.";

/* -------------------------------------------------------------------------- */
/* Datasources                                                                */
/* -------------------------------------------------------------------------- */

/** Every Ediphi datasource card clears the same way, so they say so in the same words. */
export const EDIPHI_DATASOURCE_DELETE_NOTICE =
  "Your report sheets and data tables are kept but emptied. " + DELETE_NOTICE_CLOSER;

export const ESTIMATES_LOAD_ERROR = "Error loading estimates";
export const GCGRS_LOAD_ERROR = "Error loading GCGRs";
export const BID_PACKAGES_LOAD_ERROR = "Error loading bid packages";
export const COST_MODELS_LOAD_ERROR = "Error loading cost models";

/** The card went but its workbook data did not — one per datasource, since each names its own data. */
export const ESTIMATE_REMOVED_DATA_REMAINS = "Removed the card, but the workbook still holds this estimate's data";
export const GCGR_REMOVED_DATA_REMAINS = "Removed the card, but the workbook still holds this GCGR's data";
export const CONTRACTING_REMOVED_DATA_REMAINS =
  "Removed the card, but the workbook still holds this project's contracting data";
export const COST_MODEL_REMOVED_DATA_REMAINS = "Removed the card, but the workbook still holds this cost model's data";

/* -------------------------------------------------------------------------- */
/* Project                                                                    */
/* -------------------------------------------------------------------------- */

export const PROJECT_CLEAR_ERROR = "Failed to clear this project's data";
export const PROJECT_UPDATE_ERROR = "Failed to update project data";
export const LAST_PROJECT_LOAD_ERROR = "Error loading last project and estimate";

/* -------------------------------------------------------------------------- */
/* Charts                                                                     */
/* -------------------------------------------------------------------------- */

export const CHARTS_LOAD_ERROR = "Error loading charts";
export const chartCreateError = (chartType: string) => `Error creating ${chartType} chart`;
export const chartOpenError = (title: string) => `Failed to open ${title}`;
export const chartOptionUpdateError = (option: string) => `Failed to update chart: "${option}"`;
export const chartRemoved = (title: string) => `${title} removed`;
export const chartRemoveError = (title: string) => `Failed to remove ${title}`;

/* -------------------------------------------------------------------------- */
/* Reports                                                                    */
/* -------------------------------------------------------------------------- */

export const LAST_REFRESHED = "Last refreshed";
export const LAST_REFRESHED_FORMAT = "MM/DD/YYYY h:mm A";
export const DATA_TABLES_LOADED = "Data tables loaded";
export const DATA_TABLES_REMOVED = "Data tables removed";
export const DATA_TABLES_REMOVE_ERROR = "Failed to remove data tables";
export const DATA_TABLES_LOAD_ERROR = "Failed to load data tables";
export const REPORT_REFRESH_PENDING = "Updates detected. Refreshing data first...";
export const REPORT_DATA_REFRESHED = "Data refreshed successfully";
export const TEMPLATES_DISCOVER_ERROR = "Error discovering templates";
export const reportGenerated = (worksheet: string) => `Report generated for ${worksheet}`;
export const worksheetNotFound = (worksheet: string) => `Worksheet "${worksheet}" not found`;
export const reportOptionUpdateError = (option: string) => `Failed to update option "${option}"`;
export const reportRemoved = (worksheet: string) => `${worksheet} removed`;
export const reportRemoveError = (worksheet: string) => `Failed to remove ${worksheet}`;
export const templateCreated = (template: string) => `${template} created successfully`;

/* -------------------------------------------------------------------------- */
/* Take-off                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Reports read estimate line items, never a take-off source, so removing a take-off card cannot
 * affect a report. It clears the unsubmitted selection and leaves the session signed in.
 */
export const TAKE_OFF_DELETE_NOTICE =
  "Anything you've already submitted to the estimate stays. Unsubmitted takeoff quantities are cleared. " +
  DELETE_NOTICE_CLOSER;

export const signedOutOf = (source: string) => `Signed out of ${source}`;

export const ATO_LOAD_ERROR = "Failed to load Autodesk Takeoff data";
export const ATO_REFRESH_ERROR = "Could not refresh Autodesk Takeoff data";
export const ATO_REFRESHED = "Autodesk Takeoff data refreshed";
export const ATO_HUBS_LOAD_ERROR = "Failed to load Autodesk hubs";
export const ATO_PROJECTS_LOAD_ERROR = "Failed to load projects";
export const ATO_NO_TAKEOFFS = "This project doesn't have any Autodesk Takeoffs in use";
export const ATO_NOT_ENABLED = "Autodesk Takeoffs is not enabled for this project";
export const ATO_SIGN_IN_CANCELLED = "Sign-in was cancelled or the response was invalid";
export const ATO_SIGN_IN_INVALID_RESPONSE = "Invalid response from sign-in dialog";

export const TOGAL_LOAD_ERROR = "Failed to load Togal data";
export const TOGAL_REFRESHED = "Togal data refreshed successfully";
export const TOGAL_REFRESH_PARTIAL = "Could not fully refresh Togal data";
export const TOGAL_SETS_LOAD_ERROR = "Failed to load Togal drawing sets";
export const TOGAL_PROJECTS_LOAD_ERROR = "Failed to load Togal projects";
export const TOGAL_MISSING_ORG = "Missing organization ID for Togal";
export const TOGAL_LOGIN_ERROR = "Togal login failed";

export const QTO_REVIEW_CREATED = "QTO Review sheet created";
export const QTO_REVIEW_CREATE_ERROR = "Failed to create QTO Review sheet";
export const QTO_REVIEW_NEEDS_ESTIMATE = "Please select an Estimate before submitting";

/* -------------------------------------------------------------------------- */
/* Sign in / out                                                              */
/* -------------------------------------------------------------------------- */

export const LOGIN_SUCCESS = "Login successful";
export const LOGIN_ERROR = "Login failed";
export const LOGOUT_SUCCESS = "Logged out successfully";
export const LOGOUT_ERROR = "Logout failed. Please try again.";

/* -------------------------------------------------------------------------- */
/* Import (Arcol)                                                             */
/* -------------------------------------------------------------------------- */

export const IMPORT_SUCCESS = "Import completed successfully";
export const arcolFileLoaded = (project: string, buildings: number) => `Loaded: ${project} (${buildings} buildings)`;
export const importCompletedWithErrors = (errors: number) => `Import completed with ${errors} error(s)`;

/* -------------------------------------------------------------------------- */
/* Admin tools and exports                                                    */
/* -------------------------------------------------------------------------- */

export const FLATTEN_FORMULAS_ERROR = "Failed to flatten formulas";
export const CHANGE_CURRENCY_LABEL = "Change Currency";
export const CHANGE_CURRENCY_DESCRIPTION =
  "Swaps the currency symbol on every currency-formatted cell. Only the symbol changes; values are not converted.";
export const CHANGE_CURRENCY_TITLE = "Change Currency";
export const CHANGE_CURRENCY_ERROR = "Failed to change currency";
export const CHANGE_CURRENCY_NONE_FOUND = "No currency-formatted cells found";
export const currencyChanged = (cells: number, symbol: string, where: string) =>
  `Changed ${cells.toLocaleString()} cells to ${symbol} ${where}`;
export const currencySheetsSkipped = (sheets: number) =>
  `${sheets} protected or unreadable sheet${sheets === 1 ? " was" : "s were"} skipped.`;
export const UPC_LOAD_ERROR = "Error loading UPC data";
export const UPC_LABOR_CREWS_LOAD_ERROR = "Error loading UPC labor crews";
export const MASTERFORMAT_LOAD_ERROR = "Error loading MasterFormat data";
export const UNIFORMAT_LOAD_ERROR = "Error loading Uniformat data";
export const USERS_LOAD_ERROR = "Error loading Users data";
export const BUDGET_IMPORT_SUCCESS = "Budget import created successfully";
export const PROCORE_IMPORT_SUCCESS = "Procore import file created successfully";
export const PROCORE_IMPORT_ERROR = "Failed to create Procore import file";
export const JOIN_IMPORT_SUCCESS = "Join import file created successfully";
export const SORT_FIELDS_LOAD_ERROR = "Failed to load sort fields";

/** The admin tables each word their own completion, and each reports how long it took. */
export const upcLoadedIn = (seconds: string) => `UPC data loaded successfully in ${seconds}s`;
export const upcLaborCrewsLoadedIn = (seconds: string) => `UPC labor crews loaded successfully in ${seconds}s`;
export const masterFormatCreatedIn = (seconds: string) => `MasterFormat table created successfully in ${seconds}s`;
export const uniformatCreatedIn = (seconds: string) => `Uniformat table created successfully in ${seconds}s`;
export const usersCreatedIn = (seconds: string) => `Users table created successfully in ${seconds}s`;

export const PRINT_LAYOUT_SAVE_ERROR = "Failed to save headers and footers";
export const PRINT_LAYOUT_UNSUPPORTED = "This version of Excel does not support editing headers and footers";
export const printLayoutSaved = (sheetName: string, slots: string[]) =>
  `Saved the ${slots.join(", ")} from "${sheetName}" to every sheet`;
export const printLayoutCleared = (sheetName: string) =>
  `"${sheetName}" has no {{project.name}} or {{estimate.name}} in its header or footer, so headers and footers are no longer updated`;
export const printLayoutTooLong = (sheetName: string, section: string) =>
  `The ${section} on "${sheetName}" is too long to leave room for the names. Shorten the rest of the ${section}.`;
export const printLayoutFailed = (sheets: string[]) => `Excel rejected the header or footer on ${sheets.join(", ")}`;

/* -------------------------------------------------------------------------- */
/* Dialog titles and prompts                                                  */
/* -------------------------------------------------------------------------- */

export const removeDatasourceTitle = (datasource: string) => `Remove ${datasource}?`;
export const deleteWorkbookItemTitle = (item: string) => `Delete ${item}?`;
export const REMOVE_PROJECT_TITLE = "Remove this project?";
export const deleteSheetsTitle = (count: number) => `Delete ${count} sheet${count === 1 ? "" : "s"}?`;
export const ALL_DATA_LOADED_TITLE = "All data loaded!";
export const COST_MODEL_UNAVAILABLE_TITLE = "Cost model unavailable";
/** Appended to a datasource-gone message when the card can be removed in response. */
export const REMOVE_ITS_CARD_PROMPT = "Remove its card?";

/* -------------------------------------------------------------------------- */
/* Labels, placeholders and empty states                                      */
/* -------------------------------------------------------------------------- */

export const ESTIMATES_EMPTY = "This project has no estimates.";
export const GCGRS_EMPTY = "This project has no GCGRs.";
export const COST_MODELS_EMPTY = "This project has no cost models.";
export const COST_MODELS_COMING_SOON = "Cost Model data sources are coming soon!";
export const TOGAL_SETS_EMPTY = "This project has no drawing sets.";
export const ATO_PACKAGES_EMPTY = "This project doesn't have any Autodesk Takeoff packages";

export const DATASOURCE_TYPE_LABEL = "Data source type";
export const TAKE_OFF_SOURCE_TYPE_LABEL = "External Takeoff source type";
export const ATO_HUB_LABEL = "Forma Hub";
export const SHOW_DATA_SHEETS_LABEL = "Show data sheets";
export const DATA_TABLES_LABEL = "Data Tables";
export const MULTI_SHEET_REPORT_TOOLTIP = "Multi Sheet Report";
/** Read by screen readers on the page tab bar. */
export const SUBNAV_ARIA_LABEL = "scrollable tabs with icons";

export const TOGAL_SIGN_IN_LABEL = "Log In";
export const ATO_SIGN_IN_LABEL = "Sign in with Autodesk";
export const PROCORE_SORT_FIELDS_TITLE = "Procore Import — Sort Fields";
/** The file is what the user imports into Procore, so it is named from Procore's side —
 * matching the Budget Import and Join Import buttons beside it. */
export const PROCORE_MARKUP_REUSE_COST_TYPE = "Leave empty to reuse the Cost Type sort field for markups";
export const PROCORE_MARKUP_REUSE_OTHER = "Leave empty to reuse the Other Cost Type sort field for markups";
export const PROCORE_TIER_SINGLE_LABEL = "Single Cost Type field";
export const PROCORE_TIER_MANIFOLD_LABEL = "One per LMSEO bucket";

export const CREATE_BUDGET_IMPORT_LABEL = "Create Budget Import";
export const CREATE_JOIN_IMPORT_LABEL = "Create Join Import";
export const CREATE_PROCORE_IMPORT_LABEL = "Create Procore Import";

/* -------------------------------------------------------------------------- */
/* Card tooltips and dialog bodies                                            */
/* -------------------------------------------------------------------------- */

export const refreshDatasourceTooltip = (datasource: string) => `Refresh ${datasource}`;
export const openDatasourceTooltip = (datasource: string) => `Open ${datasource}`;
export const removeDatasourceTooltip = (datasource: string) => `Remove ${datasource}`;
export const updateDatasourceTooltip = (datasource: string) => `Update ${datasource}`;
export const removeDatasourceDescription = (datasource: string) =>
  `This removes the ${datasource} card from the workbook.`;
export const deleteWorkbookItemDescription = (item: string) => `This will delete your ${item}.`;
export const removeProjectDescription = (project: string) =>
  `This will remove ${project} and clear its data from this workbook.`;
export const EMPTY_SHEETS_DELETE_DESCRIPTION = "The following sheets have no related data and will be deleted.";
export const REPORT_DELETE_NOTICE = "This removes the report from the workbook. You can generate it again anytime.";

/* -------------------------------------------------------------------------- */
/* Notices                                                                    */
/* -------------------------------------------------------------------------- */

export const CHART_DELETE_NOTICE = "This removes the chart and its hidden data sheet from the workbook.";
export const DATA_TABLES_DELETE_NOTICE = "Deleting will cause any custom reports referencing this data to break.";
export const DATA_TABLES_REFRESH_ERROR = "Failed to refresh data tables";
export const PROJECT_REMOVED_DATA_REMAINS =
  "Your report sheets and data tables are kept but emptied. You can re-select this project anytime.";
export const DATASOURCE_REMOVED_SHEETS_EMPTIED = "Your report sheets and data tables are kept but emptied.";
export const LOADER_DO_NOT_EDIT_WARNING =
  "Do not edit this workbook or switch to another workbook. Either action will cause the loading process to fail.";
export const LOADER_RETRY_PROMPT = "Please try again and contact support if the issue persists.";

/* -------------------------------------------------------------------------- */
/* Section headings                                                           */
/* -------------------------------------------------------------------------- */

export const TOOL_BOX_HEADING = "Tool Box";
export const ADMIN_TOOLS_HEADING = "Admin Tools";
export const EDIPHI_TOOLBOX_HEADING = "Ediphi Toolbox";
export const DRAWING_SET_HEADING = "Drawing Set";
export const TAKEOFF_PACKAGE_HEADING = "Takeoff Package";
export const REMOVE_PROJECT_TOOLTIP = "Remove project";

/* -------------------------------------------------------------------------- */
/* Admin tool buttons                                                         */
/* -------------------------------------------------------------------------- */

export const LOAD_UPC_TABLE_LABEL = "Load UPC Table";
export const LOAD_LABOR_CREWS_TABLE_LABEL = "Load Labor Crews Table";
export const LOAD_MASTERFORMAT_TABLE_LABEL = "Load MasterFormat Table";
export const LOAD_UNIFORMAT_TABLE_LABEL = "Load Uniformat Table";
export const LOAD_USERS_TABLE_LABEL = "Load Users Table";
export const SAVE_PRINT_LAYOUT_LABEL = "Save Headers & Footers";
export const SAVE_PRINT_LAYOUT_TOOLTIP =
  "Put {{project.name}} or {{estimate.name}} in this sheet's header or footer, then click this button. Every left, center or right part that holds one is copied to every sheet and updated whenever the project or estimate changes, replacing any edit made in Excel. Parts without one are left alone. To stop a part updating, clear it on this sheet and click again.";

/* -------------------------------------------------------------------------- */
/* Dialog action buttons                                                      */
/* -------------------------------------------------------------------------- */

/**
 * `Dialog` supplies CONFIRM (or DELETE, when destructive) and CANCEL as defaults, so most call
 * sites name no button at all. The two destructive verbs are not interchangeable: REMOVE unlinks a
 * card and leaves the workbook data in place, DELETE destroys it — and the paired notice
 * (`*_REMOVED_DATA_REMAINS` vs `*_DELETE_NOTICE`) is what tells the user which happened, so the
 * verb and the notice have to agree.
 */
export const CONFIRM_ACTION = "Confirm";
export const CANCEL_ACTION = "Cancel";
export const DELETE_ACTION = "Delete";
export const REMOVE_ACTION = "Remove";
/** Declines a remove. Pairs with REMOVE_ACTION where keeping the card is a real choice, not a dismissal. */
export const KEEP_ACTION = "Keep";
export const OK_ACTION = "OK";
/** Acknowledges an error the user cannot act on; there is no cancel half to pair with. */
export const ACKNOWLEDGE_ACTION = "Got it";

/* -------------------------------------------------------------------------- */
/* Missing datasource / QTO import outcome                                    */
/* -------------------------------------------------------------------------- */

export const DATASOURCE_NOT_FOUND_TITLE = "Data source not found";
export const datasourceGoneDescription = (datasource: string) =>
  `${datasource} no longer exists in Ediphi. ${REMOVE_ITS_CARD_PROMPT}`;
export const QTO_IMPORT_SUCCESS_TITLE = "Success";
/** Names OK_ACTION rather than hardcoding "OK" so renaming the button cannot desync the sentence. */
export const qtoImportSuccess = (rowCount: number, estimate: string) =>
  `Imported ${rowCount} line items to ${estimate}. Click ${OK_ACTION} to open the estimate in Ediphi to review your changes.`;

/* -------------------------------------------------------------------------- */
/* Datasource gone                                                            */
/* -------------------------------------------------------------------------- */

/**
 * Carried as the message of a `DatasourceGoneError` thrown from the hooks, which the loader dialog
 * and the banners read back off the error. Each names the ways out the user actually controls,
 * rather than telling them to refresh — which is what they just did.
 *
 * The sibling permission refusal is not here: `api/costModels.ts` builds that one, and nothing
 * outside `ui/` may import from inside it.
 */
export const ESTIMATE_GONE_MESSAGE =
  "This estimate no longer exists in Ediphi. Select another estimate or delete this one.";

export const GCGR_GONE_MESSAGE = "This GCGR no longer exists in Ediphi. Select another GCGR or delete this one.";

export const CONTRACTING_GONE_MESSAGE = "This project no longer has bid packages. Delete the Contracting card.";

export const COST_MODEL_GONE_MESSAGE =
  "This cost model no longer exists in Ediphi. Select another cost model or delete this one.";

/**
 * The no-card variants, for a pick from a list that turned out not to exist. Nothing was loaded from
 * it, so they offer no delete — there is no card to delete.
 */
export const ESTIMATE_PICK_GONE_MESSAGE = "This estimate no longer exists in Ediphi. Select another estimate.";

export const GCGR_PICK_GONE_MESSAGE = "This GCGR no longer exists in Ediphi. Select another GCGR.";

export const CONTRACTING_PICK_GONE_MESSAGE = "This project has no bid packages.";

export const COST_MODEL_PICK_GONE_MESSAGE = "This cost model no longer exists in Ediphi. Select another cost model.";

/* -------------------------------------------------------------------------- */
/* Loader dialog                                                              */
/* -------------------------------------------------------------------------- */

export const LOADING_ESTIMATE_TITLE = "Loading estimate data";
export const LOADING_GCGR_TITLE = "Loading GCGR data";
export const LOADING_CONTRACTING_TITLE = "Loading contracting data";
export const LOADING_COST_MODEL_TITLE = "Loading cost model data";
/** Used when a pass covers more than one datasource, so no single name fits. */
export const LOADING_DATASOURCE_TITLE = "Loading datasource data";
export const LOADING_TABLES_TITLE = "Loading table data";
export const LOADING_REPORTS_TITLE = "Loading reports";
export const LOADING_CHARTS_TITLE = "Loading charts";

export const DATASOURCE_LOAD_FAILED_TITLE = "Data failed to load";
export const TABLES_LOAD_FAILED_TITLE = "Data tables failed to load";
export const REPORTS_LOAD_FAILED_TITLE = "Reports failed to load";
export const CHARTS_LOAD_FAILED_TITLE = "Charts failed to load";
/** Shown per section when the failure carries no wording of its own worth surfacing. */
export const LOADER_DATASOURCES_ERROR = "There was an issue loading the data.";
export const LOADER_TABLES_ERROR = "There was an issue loading the data tables.";
export const LOADER_REPORTS_ERROR = "There was an issue loading the reports.";
export const LOADER_CHARTS_ERROR = "There was an issue loading the charts.";
export const WORKBOOK_EDITED_WHILE_LOADING =
  "The workbook was edited while loading. Finish your edit by pressing ENTER, TAB, or selecting another cell before proceeding.";
export const LOADER_DISMISS_NOTICE = "You can now interact with your workbook. The dialog will close automatically.";

/* -------------------------------------------------------------------------- */
/* QTO review tooltips                                                        */
/* -------------------------------------------------------------------------- */

export const QTO_REVIEW_CREATING_TOOLTIP = "Creating the QTO Review sheet…";
export const QTO_REVIEW_SUBMITTING_TOOLTIP = "Submitting to the estimate…";
export const QTO_REVIEW_LINE_ITEMS_ERROR_TOOLTIP =
  "Could not load estimate line items. Check your connection or try another estimate.";
export const QTO_REVIEW_LOADING_TOOLTIP = "Loading estimate line items to compare with takeoffs…";
export const QTO_REVIEW_NOTHING_TOOLTIP = "No additions or updates to review";
